import { useId, useState } from 'react';
import type { TodayBreakdownResponse } from '@bazaar/shared';
import { EmptyState } from '../EmptyState.js';
import { Legend, type LegendItem } from './Legend.js';
import { buildColorMap, computeYAxisTicks, formatHour12 } from './colors.js';

export interface BarChartProps {
  hours: TodayBreakdownResponse['hours'];
  totals: TodayBreakdownResponse['totals'];
  /** "category" or "product" - only used to word the aria-label/empty state. */
  groupByLabel: string;
}

const CHART_HEIGHT = 180;
const SLOT_WIDTH = 48;
const BAR_WIDTH = 22; // marks-and-anatomy: bars <= 24px thick
const MIN_SEGMENT_PX = 3; // a minimum visible height for small values
const SEGMENT_GAP = 2; // the surface-colour gap between stacked segments
const Y_AXIS_WIDTH = 34;
const HOUR_LABEL_HEIGHT = 22;
const SVG_HEIGHT = CHART_HEIGHT + HOUR_LABEL_HEIGHT + 8;

/**
 * Stacked bar chart: one bar per hour, segments coloured by category/product.
 * Inline SVG, no charting library (keeps the bundle small on mobile data).
 * The Y-axis is a separate, non-scrolling SVG so it stays visible while the
 * bars themselves scroll horizontally when there are many hours; the page
 * itself never scrolls sideways because only this inner strip does.
 */
export function BarChart({ hours, totals, groupByLabel }: BarChartProps) {
  const titleId = useId();
  const [active, setActive] = useState<{ hour: number; label: string; qty: number } | null>(null);

  if (hours.length === 0) {
    return (
      <EmptyState title="No sales yet today" description="Items sold by hour will show up here." />
    );
  }

  const colorMap = buildColorMap(totals);
  const maxTotal = Math.max(1, ...hours.map((h) => h.total));
  const ticks = computeYAxisTicks(maxTotal);
  const domainMax = ticks[ticks.length - 1]!;
  const pxPerUnit = CHART_HEIGHT / domainMax;

  const barsWidth = hours.length * SLOT_WIDTH;
  const totalSegmentCount = hours.reduce((sum, h) => sum + h.segments.length, 0);

  const ariaLabel = `Items sold by hour and ${groupByLabel}, ${formatHour12(hours[0]!.hour)} to ${formatHour12(
    hours[hours.length - 1]!.hour,
  )}. ${hours.reduce((sum, h) => sum + h.total, 0)} items total.`;

  return (
    <div>
      <div role="img" aria-labelledby={titleId} className="flex items-stretch gap-0">
        <span id={titleId} className="sr-only">
          {ariaLabel}
        </span>
        {/* Fixed Y-axis strip - does not scroll. */}
        <svg
          width={Y_AXIS_WIDTH}
          height={SVG_HEIGHT}
          viewBox={`0 0 ${Y_AXIS_WIDTH} ${SVG_HEIGHT}`}
          aria-hidden="true"
          className="shrink-0"
        >
          {ticks.map((tick) => {
            const y = CHART_HEIGHT - tick * pxPerUnit;
            return (
              <text
                key={tick}
                x={Y_AXIS_WIDTH - 6}
                y={y}
                dy="0.32em"
                textAnchor="end"
                className="font-body"
                style={{ fontSize: 11, fill: 'var(--muted)' }}
              >
                {tick}
              </text>
            );
          })}
        </svg>

        {/* Scrollable bars - the only part that ever scrolls horizontally. */}
        <div className="min-w-0 flex-1 overflow-x-auto">
          <svg
            width={barsWidth}
            height={SVG_HEIGHT}
            viewBox={`0 0 ${barsWidth} ${SVG_HEIGHT}`}
            role="presentation"
          >
            {ticks.map((tick) => {
              const y = CHART_HEIGHT - tick * pxPerUnit;
              return (
                <line
                  key={tick}
                  x1={0}
                  x2={barsWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--border)"
                  strokeWidth={1}
                />
              );
            })}

            {hours.map((hour, hourIndex) => {
              const slotX = hourIndex * SLOT_WIDTH;
              const barX = slotX + (SLOT_WIDTH - BAR_WIDTH) / 2;
              let y = CHART_HEIGHT;
              const shapes = hour.segments.map((seg, segIndex) => {
                const rawHeight = seg.qty * pxPerUnit;
                const height = rawHeight > 0 ? Math.max(rawHeight, MIN_SEGMENT_PX) : 0;
                const segY = y - height;
                const isTop = segIndex === hour.segments.length - 1;
                const fill = colorMap.get(seg.key) ?? 'var(--chart-other)';
                // The topmost segment of each bar gets the 4px rounded
                // data-end; every other segment (including the bottommost,
                // on the real baseline) is square.
                const radius = isTop ? Math.min(4, height / 2, BAR_WIDTH / 2) : 0;
                const node = (
                  <g key={seg.key}>
                    {height > 0 && (
                      <rect
                        x={barX}
                        y={segY}
                        width={BAR_WIDTH}
                        height={height}
                        rx={radius}
                        ry={radius}
                        fill={fill}
                      />
                    )}
                    <rect
                      x={slotX}
                      y={Math.min(segY, CHART_HEIGHT - 12)}
                      width={SLOT_WIDTH}
                      height={Math.max(height, 12)}
                      fill="transparent"
                      tabIndex={0}
                      role="button"
                      aria-label={`${seg.label}: ${seg.qty} at ${formatHour12(hour.hour)}`}
                      onMouseEnter={() =>
                        setActive({ hour: hour.hour, label: seg.label, qty: seg.qty })
                      }
                      onFocus={() => setActive({ hour: hour.hour, label: seg.label, qty: seg.qty })}
                      onClick={() => setActive({ hour: hour.hour, label: seg.label, qty: seg.qty })}
                    >
                      <title>{`${seg.label}: ${seg.qty} at ${formatHour12(hour.hour)}`}</title>
                    </rect>
                  </g>
                );
                y = segY - (height > 0 ? SEGMENT_GAP : 0);
                return node;
              });

              return (
                <g key={hour.hour}>
                  {shapes}
                  <text
                    x={slotX + SLOT_WIDTH / 2}
                    y={CHART_HEIGHT + HOUR_LABEL_HEIGHT - 4}
                    textAnchor="middle"
                    className="font-body"
                    style={{ fontSize: 11, fill: 'var(--muted)' }}
                  >
                    {formatHour12(hour.hour)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      <p className="min-h-5 font-body text-sm text-ink mt-1" role="status">
        {active ? `${active.label}: ${active.qty} at ${formatHour12(active.hour)}` : ' '}
      </p>

      <Legend
        items={totals.map<LegendItem>((t) => ({
          key: t.key,
          label: t.label,
          qty: t.qty,
          color: colorMap.get(t.key) ?? 'var(--chart-other)',
        }))}
      />

      {/* `sr-only` on the wrapping div, not the table itself: a table's
          intrinsic (auto) layout can render wider than an explicit 1px
          width when its cell content is wide, which would otherwise leak
          past `overflow:hidden` and widen the whole page. A block-level
          wrapper always clips its child's rendered size, regardless of the
          child's own layout algorithm. */}
      <div className="sr-only">
        <table>
          <caption>Items sold per hour by {groupByLabel}</caption>
          <thead>
            <tr>
              <th scope="col">Hour</th>
              <th scope="col">{groupByLabel === 'product' ? 'Product' : 'Category'}</th>
              <th scope="col">Quantity</th>
            </tr>
          </thead>
          <tbody>
            {hours.flatMap((hour) =>
              hour.segments.length === 0
                ? [
                    <tr key={`${hour.hour}-empty`}>
                      <td>{formatHour12(hour.hour)}</td>
                      <td>-</td>
                      <td>0</td>
                    </tr>,
                  ]
                : hour.segments.map((seg) => (
                    <tr key={`${hour.hour}-${seg.key}`}>
                      <td>{formatHour12(hour.hour)}</td>
                      <td>{seg.label}</td>
                      <td>{seg.qty}</td>
                    </tr>
                  )),
            )}
          </tbody>
        </table>
      </div>
      {/* data-testid hook for e2e / unit assertions on rendered mark count */}
      <span data-testid="bar-segment-count" hidden>
        {totalSegmentCount}
      </span>
    </div>
  );
}
